import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom-v5-compat';

import { AgentProvider } from '~/contexts/AgentContext';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { getApplicationRoute } from '~/routing/utils';

import { selectDocument } from '../ask-anything-button/selectors';
import {
  ROUTES,
  testIsInADocViewerPage,
  testIsPreSig,
  testPageHasSidebar,
} from '../ask-anything-button/utils';
import { ChatBotPanelContent } from './ChatBotPanelContent';
import { registerAgents } from './registerAgents';
import { useChatBotSideContent } from './SideContent/useChatBotSideContent';
import { useGetUserMessageActions } from './useGetUserMessageActions';
import { testEntityAndContextCompatibility } from './utils';

export const ChatBotPanel = () => {
  const dispatch = useDispatch();
  const { location } = useRouting();
  const hasAskAnything = useFlag(FlagType.AskAnything);

  const currentChatbot = useSelector(
    slices.chatbot.selectors.selectCurrentChatbot,
  );
  const isVisible = useSelector(
    slices.chatbot.selectors.selectIsChatbotVisible,
  );
  const panelMode = useSelector(slices.chatbot.selectors.selectPanelMode);
  const panelzIndex = useSelector(slices.chatbot.selectors.selectPanelZIndex);
  const isTextInputDisabled = useSelector(
    slices.chatbot.selectors.selectIsTextInputDisabled,
  );
  const pageStatusMessage = useSelector(
    slices.chatbot.selectors.selectStatusMessage,
  );
  const document = useSelector(selectDocument);
  const intl = useIntl();

  const context = useSelector(slices.chatbot.selectors.selectChatbotContext);
  const selectedContext = useSelector(
    slices.chatbot.selectors.selectChatbotSelectedContext,
  );
  const activeCitation = useSelector(
    slices.chatbot.selectors.selectActiveCitation,
  );

  const docHandlerId = useMemo(() => {
    if (!activeCitation?.link) return undefined;

    const pathname = getApplicationRoute(activeCitation.link);

    const match = matchPath(ROUTES.document, pathname);

    if (match?.params.id) {
      const id = parseInt(match.params.id);
      return isNaN(id) ? undefined : id;
    }

    return undefined;
  }, [activeCitation?.link]);

  const isDocViewerPage = useMemo(
    () => testIsInADocViewerPage(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (testPageHasSidebar(location.pathname) && panelMode === 'floating') {
      dispatch(slices.chatbot.actions.setPanelMode('floating-sidepanel'));
    } else if (
      !testPageHasSidebar(location.pathname) &&
      panelMode === 'floating-sidepanel'
    ) {
      dispatch(slices.chatbot.actions.setPanelMode('floating'));
    }
  }, [location.pathname, panelMode]);

  useEffect(() => {
    if (!isDocViewerPage) {
      dispatch(slices.chatbot.actions.setActiveSourceIndex(null));
      dispatch(slices.chatbot.actions.setSources([]));
    }
  }, [isDocViewerPage]);

  const disableInput = useMemo(() => {
    return (
      (isDocViewerPage &&
        currentChatbot?.type === 'ticket' &&
        testIsPreSig(location.pathname) &&
        !document?.isLatestVersion) ||
      isTextInputDisabled
    );
  }, [isDocViewerPage, currentChatbot, document, isTextInputDisabled]);

  const isEntityAndContextCompatible = useMemo(() => {
    return testEntityAndContextCompatibility(currentChatbot, selectedContext);
  }, [currentChatbot, selectedContext]);

  // question, question group and history tabs
  const { sideContent } = useChatBotSideContent({
    disableRun: !!disableInput,
  });

  const userMessageActions = useGetUserMessageActions({
    scope: currentChatbot?.type as
      | 'document_handler'
      | 'document_version'
      | 'all_documents',
  });

  const [clearAction, setClearAction] = useState<{
    onClick: () => void;
    isLoading: boolean;
  } | null>(null);

  const hasWorkflowRoutingAgent = useFlag(FlagType.WorkflowRoutingAgent);

  useEffect(() => {
    registerAgents(hasWorkflowRoutingAgent);
  }, [hasWorkflowRoutingAgent]);

  if (!(hasAskAnything && currentChatbot && isVisible)) {
    return null;
  }

  const statusMessage = !isEntityAndContextCompatible
    ? {
        message: intl.formatMessage({
          id: 'ChatBotPanel.contextNotCompatibleMessage',
          defaultMessage:
            'This conversation no longer aligns with your current view. Please start a new chat to continue.',
          description:
            'Message shown when the selected context is no longer compatible with the entity',
        }),
        status: 'warning' as const,
        title: intl.formatMessage({
          id: 'ChatBotPanel.contextHasChangedTitle',
          defaultMessage: 'Context has changed.',
          description:
            'Title of the banner indicating that the context has changed',
        }),
      }
    : pageStatusMessage
      ? { ...pageStatusMessage, status: 'warning' as const }
      : undefined;

  const isExpanded = panelMode === 'floating-full-width';

  return (
    <AgentProvider>
      <ChatBotPanelContent
        panelConfig={{
          mode: panelMode,
          zIndex: panelzIndex,
          isExpanded,
        }}
        clearAction={clearAction}
        setClearAction={setClearAction}
        context={context}
        selectedContext={selectedContext}
        currentChatbot={currentChatbot}
        activeCitation={activeCitation}
        docHandlerId={docHandlerId}
        statusMessage={statusMessage}
        disableInput={disableInput}
        isEntityAndContextCompatible={isEntityAndContextCompatible}
        isVisible={isVisible}
        sideContent={sideContent}
        userMessageActions={userMessageActions}
      />
    </AgentProvider>
  );
};
